import {
    GetContextByStateKeyResponse,
    GetOnlineAccountResponse
} from '@iwoca/lapi-client/edge';
import _ from 'lodash';

import { getCountry } from '../../iwIntlProvider/CountryProvider';
import {
    TDocumentType,
    TDocumentTypeDE
} from '../ui/documentRequirement/hooks/useFileUploadReducer';

function isDEBankStatementGroup(documentType: string) {
    return _.includes(
        ['bank statement', 'personal bank statement'],
        documentType
    );
}

function isDEManagementAccountsGroup(documentType: string) {
    return _.includes(
        [
            'management accounts',
            'business account',
            'pnl and balance',
            'profit and loss',
            'balance sheet',
            'management and balance accounts',
            'year end management and balance accounts',
            'year end management accounts',
            'account balances'
        ],
        documentType
    );
}

function isDEOtherGroup(documentType: string) {
    return _.includes(
        [
            'other',
            'aged debtor report',
            'vat return',
            'business plan',
            'invoices',
            'company bylaws',
            'trade certificate',
            'articles of association',
            'self assessment tax return',
            'customer broker consent',
            'cash register',
            'nif card',
            'modelo',
            'certificados'
        ],
        documentType
    );
}

function isDEShareholderID(documentType: string) {
    return _.includes(
        ['shareholder id', 'limited partner id', 'list of shareholders'],
        documentType
    );
}

export type TDocuments = {
    attributes: TAttributes;
    id: string;
    relationships?: {
        bank_account: {
            data: {
                id: string;
                type: string;
            };
        };
    };
    type: TDocumentType & TDocumentTypeDE;
}[];
//  GetDocumentUploadResponse['data'] is a partial in LAPI Client, which it probably shouldn't be. This type can be removed when that is fixed

type TAttributes = {
    bank_transactions_account_id: string;
    document_feedback?: {
        feedback_type: TFeedbackReasonType;
        id: number;
        marked_as_done_by_customer: boolean;
    }[];
    document_name: string;
    document_type: string;
    end_date?: string;
    is_verified: boolean;
    reject_reason?:
        | 'ILLEGIBLE'
        | 'PASSWORD_PROTECTED'
        | 'MISSING_TRANSACTIONS'
        | 'MISSING_ACCOUNT_DETAILS'
        | 'MISSING_BALANCES'
        | 'INCOMPLETE_REFERENCES'
        | 'OTHER'
        | 'DUPLICATE'
        | 'UNSUPPORTED_EXTENSION'
        | 'UNSUPPORTED_FORMAT'
        | 'NO_RELEVANT_DATA'
        | 'OUTDATED_INFORMATION';
    start_date?: string;
    status: 'complete' | 'rejected' | 'pending' | 'under_review';
    uploaded_at: string;
};

export type TFeedbackReasonType =
    | 'ILLEGIBLE'
    | 'INVALID_DOCUMENT_FORMAT'
    | 'PASSWORD_NOT_AVAILABLE'
    | 'REFERENCES_MISSING'
    | 'BALANCE_FIGURES_MISSING'
    | 'MISSING_TRANSACTIONS'
    | 'MISSING_APPLICANT_NAME_AND_COMPANY_NAME'
    | 'MISSING_SUMMARY_PAGE'
    | 'MISSING_LOGO_AND_IBAN'
    | 'SCREENSHOT'
    | 'ACTIVE_LINKS'
    | 'INCORRECT_ORDER';

export function groupByDocumentType(documents: TDocuments) {
    if (getCountry() !== 'DE') {
        return _.groupBy(documents, 'attributes.document_type');
    }

    const groupedDocuments = documents.map((document: TDocuments[number]) => {
        const docType = document.attributes.document_type;

        if (isDEBankStatementGroup(docType)) {
            document.attributes.document_type = 'bank statement';
        }

        if (isDEManagementAccountsGroup(docType)) {
            document.attributes.document_type = 'management accounts';
        }

        if (isDEOtherGroup(docType)) {
            document.attributes.document_type = 'other';
        }

        if (isDEShareholderID(docType)) {
            document.attributes.document_type = 'shareholder id';
        }

        return document;
    });

    return _.groupBy(groupedDocuments, 'attributes.document_type');
}

type TOnlineAccounts = GetOnlineAccountResponse['data'];
type TOpenBankingProviders = NonNullable<
    GetContextByStateKeyResponse['data']
>['open_banking']['banks'];

export function deserialiseLinkedOpenBankingAccounts(
    onlineAccounts: TOnlineAccounts,
    openBankingProviders: TOpenBankingProviders
) {
    const linkedAccounts = onlineAccounts;
    const openBankingProviderNames =
        pluckOpenBankingProviderNames(openBankingProviders);

    return linkedAccounts
        .filter((linkedAccount) =>
            filterLinkedOpenBankingAccounts(
                linkedAccount,
                openBankingProviderNames
            )
        )
        .filter(filterExpiredLinkedOpenBankingAccounts)
        .map((linkedAccount) => {
            return addBankNameToLinkedAccount(
                linkedAccount,
                openBankingProviders
            );
        })
        .map(deserialiseLinkedOpenBankingAccount);
}

function filterExpiredLinkedOpenBankingAccounts(
    linkedOpenBankingAccount: TOnlineAccounts[number]
) {
    if (!linkedOpenBankingAccount.status) return false;
    if (linkedOpenBankingAccount.status.code !== 'active') return false;

    return true;
}

function deserialiseLinkedOpenBankingAccount(
    linkedOpenBankingAccount: TOnlineAccounts[number]
) {
    const connectedAccounts = linkedOpenBankingAccount.accounts;

    return {
        // @ts-expect-error bank_name doesn't exist on object. Needs removing/fixing
        bankName: linkedOpenBankingAccount.bank_name,
        providerId: linkedOpenBankingAccount.online_account_id,
        accountServiceProviderId: linkedOpenBankingAccount.provider,
        linkedAt: linkedOpenBankingAccount.linked_at,
        accounts: connectedAccounts.map(deserialiseAccount),
        status: linkedOpenBankingAccount.status?.code,
        // @ts-expect-error bank_name doesn't exist on object. Needs removing/fixing
        logo_url: linkedOpenBankingAccount.logo_url
    };
}

function deserialiseAccount(
    onlineAccount: TOnlineAccounts[number]['accounts'][number]
) {
    const extraAccountInformation = onlineAccount.extra || {};

    return {
        name: onlineAccount.name,
        balance: extraAccountInformation.balance,
        accountNumber: extraAccountInformation.account_number
    };
}

function pluckOpenBankingProviderNames(
    openBankingProviders: TOpenBankingProviders
) {
    return openBankingProviders.map((provider) => provider.bank_name);
}

function filterLinkedOpenBankingAccounts(
    linkedAccount: TOnlineAccounts[number],
    openBankingProviderNames: string[]
) {
    return openBankingProviderNames.includes(linkedAccount.provider!);
}

function addBankNameToLinkedAccount(
    linkedAccount: TOnlineAccounts[number],
    openBankingProviders: TOpenBankingProviders
) {
    const accountBank = _.find(openBankingProviders, [
        'bank_name',
        linkedAccount.provider
    ]);
    // @ts-expect-error bank_name doesn't exist on object. Needs removing/fixing
    linkedAccount.bank_name = accountBank.bank_name;

    return linkedAccount;
}
